import { put, takeLatest, call } from "redux-saga/effects";
import AuthActions, { Types as AuthTypes } from "../../stores/auth/Actions";
import AuthService from "../../services/auth/AuthService";
import PrestadorService from "../../services/prestador/PrestadorService";
import ResponseCode from "../../services/ResponseCode";
import { jwtToObject } from "../../utils/ObjectUtil";
import { LOCAL_STORAGE, PSS_TYPE } from "../../utils/Constant";
import { RESPUESTA_PILOTO_CIERRE_CUENTAS } from "../../containers/ingreso/utils/IngresoConstant";

function* authUser(request) {
  yield put(AuthActions.authUserLoading());

  const { codigo, pin } = request.payload;

  const requestToken = {
    username: codigo,
    password: pin,
    compania: process.env.REACT_APP_COMPANY,
  };

  const tokenResponse = yield call(AuthService.getToken, requestToken);

  if (!tokenResponse?.respuesta) {
    yield put(
      AuthActions.authUserFailure({
        codigo: ResponseCode.SERVER_ERROR.code,
        mensaje: ResponseCode.SERVER_ERROR.message,
      })
    );
    return;
  }

  if (
    tokenResponse?.respuesta?.codigo !== ResponseCode.USER_AUTHENTICATED.code
  ) {
    yield put(
      AuthActions.authUserFailure({
        codigo: ResponseCode.AUTHENTICATION_FAILED.code,
        mensaje: ResponseCode.AUTHENTICATION_FAILED.message,
      })
    );
    return;
  }

  const sesionResponse = yield call(
    AuthService.newSession,
    AuthService.handleRequestAuthPin(
      { codigo },
      tokenResponse?.data?.accessToken,
      pin
    )
  );

  if (!sesionResponse?.respuesta) {
    yield put(
      AuthActions.authUserFailure({
        codigo: ResponseCode.SERVER_ERROR.code,
        mensaje: ResponseCode.SERVER_ERROR.message,
      })
    );
    return;
  }

  if (sesionResponse?.respuesta?.codigo === ResponseCode.SERVER_ERROR.code) {
    yield put(
      AuthActions.authUserFailure({
        codigo: ResponseCode.SERVER_ERROR.code,
        mensaje: ResponseCode.SERVER_ERROR.message,
      })
    );
    return;
  }

  if (sesionResponse?.respuesta?.codigo !== ResponseCode.OK.code) {
    yield put(AuthActions.authUserFailure(sesionResponse?.respuesta));
    return;
  }

  const { numeroSesion } = sesionResponse?.data;

  const userResponse = yield call(
    AuthService.getUserInfo,
    AuthService.handleRequestAuth(null, tokenResponse?.data?.accessToken)
  );

  if (!userResponse?.respuesta) {
    yield put(
      AuthActions.authUserFailure({
        codigo: ResponseCode.SERVER_ERROR.code,
        mensaje: ResponseCode.SERVER_ERROR.message,
      })
    );
    return;
  }

  if (userResponse?.respuesta?.codigo === ResponseCode.SERVER_ERROR.code) {
    yield put(
      AuthActions.authUserFailure({
        codigo: ResponseCode.SERVER_ERROR.code,
        mensaje: ResponseCode.SERVER_ERROR.message,
      })
    );
    return;
  }

  if (userResponse?.respuesta?.codigo !== ResponseCode.OK.code) {
    yield put(AuthActions.authUserFailure(userResponse?.respuesta));
    return;
  }

  if (Number(userResponse?.data?.prestador?.codigo) !== Number(codigo)) {
    yield put(
      AuthActions.authUserFailure({
        codigo: ResponseCode.AUTHENTICATION_FAILED.code,
        mensaje: ResponseCode.AUTHENTICATION_FAILED.message,
      })
    );
    return;
  }

  const requestConfigPaperFree = {
    codigoPss: codigo,
    tipoPss: userResponse?.data?.prestador?.tipoPss,
  };

  const configuracionPaperFree = yield call(
    AuthService.getConfiguracionPaperFree,
    AuthService.handleRequestAuth(
      requestConfigPaperFree,
      tokenResponse?.data?.accessToken
    )
  );

  const tokenData = jwtToObject(tokenResponse?.data?.accessToken);

  const isStandVirtual = tokenData?.standVirtual === "true";

  let tokenStandVirtual = null;

  if (isStandVirtual) {
    const requestTokenStandVirtual = {
      user: String(codigo),
      password: String(pin),
      isInternal: false,
    };

    const tokenStandVirtualResponse = yield call(
      AuthService.authStandVirtual,
      requestTokenStandVirtual
    );

    tokenStandVirtual = tokenStandVirtualResponse?.data?.tid;
  }

  let esPaquete = false;

  let nivelAtencionPermitido = false;

  if (
    PSS_TYPE.NO_MEDICO.toLocaleLowerCase() ===
    userResponse?.data?.prestador?.tipoPss?.toLocaleLowerCase()
  ) {
    const validarPaqueteRequest = {
      codigo: userResponse?.data?.prestador?.codigo,
    };

    const validarPaqueteResponse = yield call(
      PrestadorService.validarPaquete,
      validarPaqueteRequest
    );

    esPaquete = validarPaqueteResponse?.data?.esPaquete;

    const validarNivelRequest = {
      tipoReclamante: PSS_TYPE.NO_MEDICO,
      reclamante: userResponse?.data?.prestador?.codigo,
    };

    const validarNivelResponse = yield call(
      PrestadorService.validarNivelAtencion,
      validarNivelRequest
    );

    nivelAtencionPermitido =
      validarNivelResponse?.data?.categoria &&
      userResponse?.data?.prestador?.esCierreCuenta === RESPUESTA_PILOTO_CIERRE_CUENTAS.SI_APLICA;
  }

  const userData = {
    prestador: {
      ...userResponse?.data?.prestador,
      esPaquete,
      permiteCierreCuenta:
        userResponse?.data?.prestador?.tipoPss?.toLowerCase() ===
          PSS_TYPE.NO_MEDICO.toLowerCase() && nivelAtencionPermitido,
    },
    ccPermitido: userResponse?.data?.prestador?.Cc,
    configuracionPaperFree,
    isAuth: true,
    numeroSesionReutilizable: numeroSesion,
    isStandVirtual,
    tokenStandVirtual,
  };

  yield put(AuthActions.authUserSuccess(userData));
}

function* nuevaSesion(request) {
  yield put(AuthActions.nuevaSesionLoading());

  const { codigo } = request.payload;

  const newSessionRequest = {
    codigo,
  };

  const sesionResponse = yield call(
    AuthService.newSessionWithNoHeader,
    newSessionRequest
  );

  if (
    !sesionResponse?.respuesta ||
    sesionResponse?.respuesta?.codigo === ResponseCode.SERVER_ERROR.code
  ) {
    yield put(
      AuthActions.nuevaSesionFailure({
        codigo: ResponseCode.SERVER_ERROR.code,
        mensaje: ResponseCode.SERVER_ERROR.message,
      })
    );
    return;
  }

  if (sesionResponse?.respuesta?.codigo !== ResponseCode.OK.code) {
    yield put(AuthActions.nuevaSesionFailure(sesionResponse?.respuesta));
    return;
  }

  const { numeroSesion } = sesionResponse?.data;

  const { respuesta } = sesionResponse;

  yield put(AuthActions.nuevaSesionSuccess({ numeroSesion, respuesta }));
}

function* logoutUser() {
  yield call(AuthService.logOut, { compania: process.env.REACT_APP_COMPANY });

  localStorage.clear();

  localStorage.removeItem(LOCAL_STORAGE.ESTADO_SESION.KEY);

  localStorage.removeItem(LOCAL_STORAGE.PSS_ACTIVO.KEY);

  yield put(AuthActions.logoutUserSuccess({}));

  AuthService.redirectToLogin();
}

function* clearSesion() {
  yield put(AuthActions.clearSesionSuccess({}));
}

function* clearLogin() {
  localStorage.removeItem(LOCAL_STORAGE.ESTADO_SESION.KEY);

  localStorage.removeItem(LOCAL_STORAGE.PSS_ACTIVO.KEY);

  yield put(AuthActions.clearLoginSuccess({}));
}

const exportedArray = [
  takeLatest(AuthTypes.AUTH_USER, authUser),
  takeLatest(AuthTypes.NUEVA_SESION, nuevaSesion),
  takeLatest(AuthTypes.LOGOUT_USER, logoutUser),
  takeLatest(AuthTypes.CLEAR_SESION, clearSesion),
  takeLatest(AuthTypes.CLEAR_LOGIN, clearLogin),
];

export default exportedArray;
